import React, { forwardRef, useImperativeHandle } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { Input, Select } from "../ui/Input";
import { auditApi } from "../../services/api";
import type {
  AuditEntry,
  AuditEntryWithCategory,
  FlowAuditFilters,
} from "../../types/audit";

interface Props {
  scriptId: number;
  onRefresh?: () => void;
}

export interface FlowAuditSectionRef {
  refresh: () => void;
}

const categoryLabels: Record<AuditEntryWithCategory["category"], string> = {
  scripts: "Script",
  nodes: "Node",
  transitions: "Transition",
  condition_rules: "Condition Rule",
  other: "Other",
};

const badgeColor: Record<AuditEntryWithCategory["category"], string> = {
  scripts: "bg-blue-100 text-blue-800",
  nodes: "bg-purple-100 text-purple-800",
  transitions: "bg-amber-100 text-amber-800",
  condition_rules: "bg-teal-100 text-teal-800",
  other: "bg-gray-100 text-gray-700",
};

// Helpers copied from AuditInfo to render diffs
const tryParseJson = (s?: string | null) => {
  if (!s) return null;
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
};
const tryParseNestedJson = (value: unknown) => {
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (
      (trimmed.startsWith("{") && trimmed.endsWith("}")) ||
      (trimmed.startsWith("[") && trimmed.endsWith("]"))
    ) {
      try {
        return JSON.parse(trimmed);
      } catch {
        return value;
      }
    }
  }
  return value;
};
const extractBodyRaw = (e: AuditEntry | null): unknown => {
  if (!e) return "";
  const obj = tryParseJson(e.details);

  // If parsing succeeded, check for nested 'body' field (middleware format)
  if (obj && typeof obj === "object") {
    if ((obj as any).body !== undefined) {
      // Middleware format: { method, path, query, body, status }
      return tryParseNestedJson((obj as any).body);
    }
    // Service layer format: direct object with fields
    // Return the parsed object directly
    return obj;
  }

  // Fallback: couldn't parse, try to parse the raw string
  return tryParseNestedJson(e.details);
};
const areValuesEqual = (a: unknown, b: unknown): boolean => {
  if (a === b) return true;
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return String(a) === String(b);
  }
};
const formatValue = (val: unknown): string => {
  if (val === null || val === undefined) return "" + val;
  if (typeof val === "string") return val;
  if (typeof val === "number" || typeof val === "boolean") return String(val);
  if (val instanceof Date) return val.toISOString();
  try {
    const str = JSON.stringify(val);
    return str.length > 200 ? str.slice(0, 200) + "…" : str;
  } catch {
    return String(val);
  }
};
const renderKeyValueList = (
  data: unknown,
  diffAgainst?: unknown,
  variant: "before" | "now" = "now",
) => {
  const changedClass = variant === "before" ? "bg-red-200" : "bg-green-200";
  if (
    typeof data === "string" ||
    typeof data === "number" ||
    typeof data === "boolean" ||
    data == null
  ) {
    const changed =
      diffAgainst !== undefined && !areValuesEqual(data, diffAgainst);
    return (
      <ul className="mt-1 bg-gray-50 p-2 rounded overflow-auto text-[11px] space-y-1">
        <li className={changed ? `${changedClass} rounded px-1` : undefined}>
          <span className="text-gray-500">value:</span>{" "}
          <span className="font-mono break-all">{formatValue(data)}</span>
        </li>
      </ul>
    );
  }
  if (Array.isArray(data)) {
    const diffArray = Array.isArray(diffAgainst) ? diffAgainst : ([] as any[]);
    return (
      <ul className="mt-1 bg-gray-50 p-2 rounded overflow-auto text-[11px] space-y-1">
        {data.map((item, index) => {
          const changed =
            diffAgainst !== undefined &&
            !areValuesEqual(item, (diffArray as any)[index]);
          return (
            <li
              key={index}
              className={changed ? `${changedClass} rounded px-1` : undefined}
            >
              <span className="text-gray-500">[{index}]</span>{" "}
              <span className="font-mono break-all">{formatValue(item)}</span>
            </li>
          );
        })}
      </ul>
    );
  }
  if (typeof data === "object") {
    const entries = Object.entries(data as Record<string, unknown>);
    const diffObj =
      diffAgainst &&
      typeof diffAgainst === "object" &&
      !Array.isArray(diffAgainst)
        ? (diffAgainst as Record<string, unknown>)
        : undefined;
    return (
      <ul className="mt-1 bg-gray-50 p-2 rounded overflow-auto text-[11px] space-y-1">
        {entries.length === 0 ? (
          <li className="text-gray-400">(empty)</li>
        ) : (
          entries.map(([key, val]) => {
            const beforeVal = diffObj ? (diffObj as any)[key] : undefined;
            const changed =
              diffObj !== undefined && !areValuesEqual(val, beforeVal);
            return (
              <li
                key={key}
                className={changed ? `${changedClass} rounded px-1` : undefined}
              >
                <span className="text-gray-500">{key}:</span>{" "}
                <span className="font-mono break-all">{formatValue(val)}</span>
              </li>
            );
          })
        )}
      </ul>
    );
  }
  return (
    <ul className="mt-1 bg-gray-50 p-2 rounded overflow-auto text-[11px] space-y-1">
      <li>
        <span className="text-gray-500">value:</span>{" "}
        <span className="font-mono break-all">{formatValue(String(data))}</span>
      </li>
    </ul>
  );
};

const FlowAuditSection = forwardRef<FlowAuditSectionRef, Props>(
  ({ scriptId }, ref) => {
    const [open, setOpen] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const [items, setItems] = React.useState<AuditEntryWithCategory[]>([]);
    const [total, setTotal] = React.useState(0);
    const [page, setPage] = React.useState(1);
    const [pageSize, setPageSize] = React.useState(20);
    const [category, setCategory] =
      React.useState<FlowAuditFilters["category"]>("");
    const [search, setSearch] = React.useState("");
    const [userEmail, setUserEmail] = React.useState("");
    const [dateFrom, setDateFrom] = React.useState("");
    const [dateTo, setDateTo] = React.useState("");
    const [expandedId, setExpandedId] = React.useState<number | null>(null);
    const [expandedBefore, setExpandedBefore] =
      React.useState<AuditEntry | null>(null);
    const [expandedNow, setExpandedNow] = React.useState<AuditEntry | null>(
      null,
    );
    const [expandedLoading, setExpandedLoading] = React.useState(false);
    const [expandedError, setExpandedError] = React.useState<string | null>(
      null,
    );

    useImperativeHandle(ref, () => ({
      refresh: () => {
        load();
      },
    }));

    const load = React.useCallback(async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await auditApi.listFlow(scriptId, {
          page,
          page_size: open ? pageSize : 1,
          category: category || undefined,
          search: search || undefined,
          user_email: userEmail || undefined,
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
        });
        setItems(res.items || []);
        setTotal(res.total || 0);
      } catch (e: any) {
        setError(e?.message || "Failed to load audits");
      } finally {
        setLoading(false);
      }
    }, [
      scriptId,
      page,
      pageSize,
      open,
      category,
      search,
      userEmail,
      dateFrom,
      dateTo,
    ]);

    React.useEffect(() => {
      load();
    }, [load]);

    const toggle = () => {
      setOpen((o) => !o);
      setPage(1);
      setExpandedId(null);
    };

    const latest = items[0] || null;

    const fetchBeforeNowFor = async (entry: AuditEntryWithCategory) => {
      setExpandedLoading(true);
      setExpandedError(null);
      try {
        let resource: string | null = null;
        let id: number | null = null;
        if (entry.category === "scripts") {
          resource = "scripts";
          // target_id is the script id or embedded in action path
          id = (() => {
            const m = entry.action.match(/\/scripts\/(\d+)/);
            return m ? Number(m[1]) : (entry.target_id ?? null);
          })();
        } else if (entry.category === "nodes") {
          resource = "nodes";
          id = entry.target_id ?? null;
        } else if (entry.category === "transitions") {
          resource = "transitions";
          const m = entry.action.match(/\/transitions\/(\d+)/);
          id = m ? Number(m[1]) : (entry.target_id ?? null);
        } else if (entry.category === "condition_rules") {
          resource = "condition-rules";
          const m = entry.action.match(/\/condition-rules\/(\d+)/);
          id = m ? Number(m[1]) : (entry.target_id ?? null);
        }
        if (!resource || !id) {
          setExpandedError("Cannot infer resource/id for diff");
          setExpandedBefore(null);
          setExpandedNow(null);
          return;
        }
        const two = await auditApi.getLastTwo(resource, id);
        setExpandedBefore(two[1] || null);
        setExpandedNow(two[0] || null);
      } catch (e: any) {
        setExpandedError(e?.message || "Failed to load diff");
        setExpandedBefore(null);
        setExpandedNow(null);
      } finally {
        setExpandedLoading(false);
      }
    };

    const onExpandClick = (entry: AuditEntryWithCategory) => {
      const nextId = expandedId === entry.id ? null : entry.id;
      setExpandedId(nextId);
      setExpandedBefore(null);
      setExpandedNow(null);
      if (nextId) {
        fetchBeforeNowFor(entry);
      }
    };

    return (
      <div className="bg-white shadow rounded-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Audit logs</h3>
            <p className="text-sm text-gray-500">
              Changes across the flow, including script, nodes, transitions and
              condition rules.
            </p>
          </div>
          <div className="flex items-center gap-3">
            {open ? (
              <button
                onClick={toggle}
                className="inline-flex items-center px-3 py-1.5 rounded-md border border-gray-300 text-sm font-medium bg-white hover:bg-gray-50"
              >
                <ChevronUp className="h-4 w-4 mr-1" /> Hide
              </button>
            ) : (
              <button
                onClick={toggle}
                className="inline-flex items-center px-3 py-1.5 rounded-md border border-gray-300 text-sm font-medium bg-white hover:bg-gray-50"
              >
                <ChevronDown className="h-4 w-4 mr-1" /> Show
              </button>
            )}
          </div>
        </div>

        {!open && (
          <div className="px-6 py-4 text-sm text-gray-700">
            {loading ? (
              <div className="text-gray-500">Loading…</div>
            ) : error ? (
              <div className="text-red-600">{error}</div>
            ) : latest ? (
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${badgeColor[latest.category]}`}
                >
                  {categoryLabels[latest.category]}
                </span>
                <span className="text-gray-500">When:</span>
                <span className="font-medium">
                  {new Date(latest.timestamp).toLocaleString()}
                </span>
                <span className="text-gray-500">by</span>
                <span className="font-medium">{latest.user_email || "-"}</span>
                <span className="text-gray-500">action</span>
                <span className="font-medium">{latest.description || "-"}</span>
                <span className="text-gray-400">({latest.action})</span>
              </div>
            ) : (
              <div className="text-gray-500">No audit entries</div>
            )}
          </div>
        )}

        {open && (
          <div className="p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              <div>
                <Select
                  label="Category"
                  value={category || ""}
                  onChange={(e) => {
                    setCategory((e.target.value || "") as any);
                    setPage(1);
                  }}
                  variant="small"
                  showLabel={false}
                  className="text-xs"
                >
                  <option value="">All</option>
                  <option value="scripts">Script</option>
                  <option value="nodes">Node</option>
                  <option value="transitions">Transition</option>
                  <option value="condition_rules">Condition Rule</option>
                </Select>
              </div>
              <div>
                <Input
                  label="Search"
                  type="text"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  placeholder="Search action or description"
                  variant="small"
                  showLabel={false}
                  className="text-xs"
                />
              </div>
              <div>
                <Input
                  label="User email"
                  type="text"
                  value={userEmail}
                  onChange={(e) => {
                    setUserEmail(e.target.value);
                    setPage(1);
                  }}
                  placeholder="email contains…"
                  variant="small"
                  showLabel={false}
                  className="text-xs"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Input
                    label="From"
                    type="date"
                    value={dateFrom}
                    onChange={(e) => {
                      setDateFrom(e.target.value);
                      setPage(1);
                    }}
                    variant="small"
                    showLabel={false}
                    className="text-xs"
                  />
                </div>
                <div>
                  <Input
                    label="To"
                    type="date"
                    value={dateTo}
                    onChange={(e) => {
                      setDateTo(e.target.value);
                      setPage(1);
                    }}
                    variant="small"
                    showLabel={false}
                    className="text-xs"
                  />
                </div>
              </div>
            </div>

            <div className="text-xs text-gray-500">{total} total</div>

            <div className="border border-gray-200 rounded divide-y">
              {loading ? (
                <div className="p-3 text-sm text-gray-500">Loading…</div>
              ) : error ? (
                <div className="p-3 text-sm text-red-600">{error}</div>
              ) : items.length === 0 ? (
                <div className="p-3 text-sm text-gray-500">
                  No audit entries
                </div>
              ) : (
                items.map((it) => (
                  <div key={it.id} className="p-3 text-sm">
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${badgeColor[it.category]}`}
                      >
                        {categoryLabels[it.category]}
                      </span>
                      <span className="text-gray-500">When:</span>
                      <span className="font-medium">
                        {new Date(it.timestamp).toLocaleString()}
                      </span>
                      <span className="text-gray-500">by</span>
                      <span className="font-medium">
                        {it.user_email || "-"}
                      </span>
                      <span className="text-gray-500">action</span>
                      <span className="font-medium">
                        {it.description || "-"}
                      </span>
                      <span className="text-gray-400">({it.action})</span>
                      <button
                        onClick={() => onExpandClick(it)}
                        className="ml-auto text-blue-600 hover:text-blue-800"
                      >
                        {expandedId === it.id ? "Hide diff" : "Show diff"}
                      </button>
                    </div>
                    {expandedId === it.id && (
                      <div className="mt-2 grid grid-cols-1 gap-2">
                        {expandedLoading ? (
                          <div className="text-gray-500">Loading diff…</div>
                        ) : expandedError ? (
                          <div className="text-red-600">{expandedError}</div>
                        ) : (
                          <>
                            {expandedBefore ? (
                              <div>
                                <div className="text-xs font-medium text-gray-600">
                                  Before
                                </div>
                                {renderKeyValueList(
                                  extractBodyRaw(expandedBefore),
                                  extractBodyRaw(expandedNow),
                                  "before",
                                )}
                              </div>
                            ) : null}
                            {expandedNow ? (
                              <div>
                                <div className="text-xs font-medium text-gray-600">
                                  {expandedBefore ? "Now" : "Data"}
                                </div>
                                {renderKeyValueList(
                                  extractBodyRaw(expandedNow),
                                  expandedBefore
                                    ? extractBodyRaw(expandedBefore)
                                    : undefined,
                                  "now",
                                )}
                              </div>
                            ) : expandedBefore === null &&
                              expandedNow === null ? (
                              <div className="text-gray-500 text-xs">
                                No detailed data available for this operation.
                              </div>
                            ) : null}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>

            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1 || loading}
                  className="px-2 py-1 border rounded disabled:opacity-50"
                >
                  Prev
                </button>
                <span className="text-gray-600">Page {page}</span>
                <button
                  onClick={() => {
                    const maxPage = Math.max(1, Math.ceil(total / pageSize));
                    setPage((p) => Math.min(maxPage, p + 1));
                  }}
                  disabled={page * pageSize >= total || loading}
                  className="px-2 py-1 border rounded disabled:opacity-50"
                >
                  Next
                </button>
              </div>
              <div>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value) || 20);
                    setPage(1);
                  }}
                  className="border rounded px-2 py-1"
                >
                  <option value={10}>10 / page</option>
                  <option value={20}>20 / page</option>
                  <option value={50}>50 / page</option>
                  <option value={100}>100 / page</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  },
);

FlowAuditSection.displayName = "FlowAuditSection";

export default FlowAuditSection;
